// **Die Oberfläche nach der Anmeldung**: Rahmen, eine Ansicht je Route,
// Meldungen. Jede Ansicht ist nach ihrem Pfad geschlüsselt — ein Seitenwechsel
// baut sie neu (frischer Formularzustand), wie es die Vanilla-Fassung tat;
// was sie lädt, zeigt sie aus dem Cache sofort und holt es dabei frisch.

import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { Fragment, Suspense } from "react";
import type { Me } from "../api/types";
import { matchRoute, type Route } from "../router/routes";
import { RouterProvider, useLocation } from "../router/router";
import { Loading } from "../ui/badge";
import { Toaster } from "../ui/toaster";
import { AuditView } from "../views/audit";
import { CostsView } from "../views/costs";
import { DashboardView } from "../views/dashboard/dashboard";
import { DetailView } from "../views/detail/detail";
import { FeedbackListView } from "../views/feedback/feedback-list";
import { FeedbackReportView } from "../views/feedback/feedback-report";
import { LicenseEditorView } from "../views/licenses/license-editor";
import { LicensesView } from "../views/licenses/licenses";
import { TenantsView } from "../views/tenants";
import { VersionsView } from "../views/versions";
import { WizardView } from "../views/wizard/wizard";
import { NotFound } from "./not-found";
import { Shell } from "./shell";

export function routeElement(route: Route) {
  switch (route.name) {
    case "dashboard": return <DashboardView />;
    case "tenants": return <TenantsView />;
    case "wizard-new": return <WizardView id={null} step={1} />;
    case "wizard": return <WizardView id={route.id} step={route.step} />;
    case "detail": return <DetailView id={route.id} tab={route.tab} />;
    case "licenses": return <LicensesView />;
    case "license": return <LicenseEditorView id={route.id} />;
    case "feedback": return <FeedbackListView />;
    case "feedback-report": return <FeedbackReportView id={route.id} />;
    case "costs": return <CostsView />;
    case "audit": return <AuditView />;
    case "versions": return <VersionsView />;
    case "not-found": return <NotFound />;
  }
}

function RouteView() {
  const { pathname } = useLocation();
  return <Fragment key={pathname}>{routeElement(matchRoute(pathname))}</Fragment>;
}

export function App({ client, me }: { client: QueryClient; me: Me }) {
  return (
    <QueryClientProvider client={client}>
      <RouterProvider>
        <Shell me={me}>
          <Suspense fallback={<Loading />}>
            <RouteView />
          </Suspense>
        </Shell>
        <Toaster />
      </RouterProvider>
    </QueryClientProvider>
  );
}
